import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Layout } from '@/components/Layout';
import { HomePage } from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import { Loader2 } from 'lucide-react';

// Lazy load heavy pages
const OptionsChainPage = lazy(() => import('@/pages/OptionsChainPage'));
const VolatilitySurfacePage = lazy(() => import('@/pages/VolatilitySurfacePage'));
const SkewAnalysisPage = lazy(() => import('@/pages/SkewAnalysisPage'));
const TradeInitiationPage = lazy(() => import('@/pages/TradeInitiationPage'));
const LambdaAlgoPage = lazy(() => import('@/pages/LambdaAlgoPage'));
// Trade Initiation v2 and the compare page are archived (owner 2026-09-27) — see frontend/src/pages/archive/README.md
const StrategyBuilderPage = lazy(() => import('@/pages/StrategyBuilderPage'));
const CrawlerAnalysisPage = lazy(() => import('@/pages/CrawlerAnalysisPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const CrawlerCalculatorPage = lazy(() => import('@/pages/CrawlerCalculatorPage'));
const CrawlerAlgoPage = lazy(() => import('@/pages/CrawlerAlgoPage'));
const ClimbAlgoPage = lazy(() => import('@/pages/ClimbAlgoPage'));
const LeverageBuyTriggersPage = lazy(() => import('@/pages/LeverageBuyTriggersPage'));
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'));
const ActivityLogPage = lazy(() => import('@/pages/ActivityLogPage'));
const AccountManagementPage = lazy(() => import('@/pages/AccountManagementPage'));
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'));
const GuidesPage = lazy(() => import('@/pages/GuidesPage'));
const NightingaleV2Page = lazy(() => import('@/pages/NightingaleV2Page'));
const Nightingale5Page = lazy(() => import('@/pages/Nightingale5Page'));
const LeastActionPage = lazy(() => import('@/pages/LeastActionPage'));
const LastActionPage = lazy(() => import('@/pages/LastActionPage'));
const ClusterActionPage = lazy(() => import('@/pages/ClusterActionPage'));
const MaximisationPage = lazy(() => import('@/pages/MaximisationPage'));
const Pair23Page = lazy(() => import('@/pages/Pair23Page'));
const ExpiryTransferPage = lazy(() => import('@/pages/ExpiryTransferPage'));
const IndexReturnsPage = lazy(() => import('@/pages/IndexReturnsPage'));
const CandleChartsPage = lazy(() => import('@/pages/CandleChartsPage'));
const TasksPage = lazy(() => import('@/pages/TasksPage'));
const DevWorkflowPage = lazy(() => import('@/pages/DevWorkflowPage'));

const App: React.FC = () => {
  const { isAuthenticated, isLoading, checkPlatformSession, user } = useAuthStore();

  // Load the active market profile (currency, locale, lot size, …) once
  // authenticated — single source of truth from /api/market-profile.
  useEffect(() => {
    if (isAuthenticated) {
      import('@/utils/format').then(({ loadMarketProfile }) => loadMarketProfile());
    }
  }, [isAuthenticated]);

  // Root Level Master Lock: Check platform session only once per page load
  useEffect(() => {
    if (!(window as any)._hasCheckedInitialSession) {
      (window as any)._hasCheckedInitialSession = true;
      checkPlatformSession();
    }
  }, [checkPlatformSession]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground font-medium">Initializing Platform...</p>
        </div>
      </div>
    );
  }

  // Logged-out visitors only ever see the login page. No authenticated route
  // exists in the tree, so nothing from behind the login can render or be
  // reached by URL.
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Anything else goes to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Already authenticated: the login page just redirects into the app */}
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/* Protected Routes Wrapper */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route
          path="options-chain"
          element={
            <Suspense fallback={<Loader />}>
              <OptionsChainPage />
            </Suspense>
          }
        />
        <Route
          path="volatility"
          element={
            <Suspense fallback={<Loader />}>
              <VolatilitySurfacePage />
            </Suspense>
          }
        />
        <Route
          path="skew"
          element={
            <Suspense fallback={<Loader />}>
              <SkewAnalysisPage />
            </Suspense>
          }
        />
        <Route
          path="trade-initiation"
          element={
            <Suspense fallback={<Loader />}>
              <TradeInitiationPage />
            </Suspense>
          }
        />
        <Route
          path="lambda-algo"
          element={
            <Suspense fallback={<Loader />}>
              <LambdaAlgoPage />
            </Suspense>
          }
        />
        <Route
          path="charts"
          element={
            <Suspense fallback={<Loader />}>
              <CandleChartsPage />
            </Suspense>
          }
        />
        <Route
          path="strategy-builder"
          element={
            <Suspense fallback={<Loader />}>
              <StrategyBuilderPage />
            </Suspense>
          }
        />
        <Route
          path="crawler-analysis"
          element={
            <Suspense fallback={<Loader />}>
              <CrawlerAnalysisPage />
            </Suspense>
          }
        />
        <Route
          path="settings"
          element={
            <Suspense fallback={<Loader />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="crawler-calculator"
          element={
            <Suspense fallback={<Loader />}>
              <CrawlerCalculatorPage />
            </Suspense>
          }
        />
        <Route
          path="crawler-algo"
          element={
            <Suspense fallback={<Loader />}>
              <CrawlerAlgoPage />
            </Suspense>
          }
        />
        {/* Merged crawlers + leverage buys, preview (owner 2026-10-14). */}
        <Route
          path="climb-algo"
          element={
            <Suspense fallback={<Loader />}>
              <ClimbAlgoPage />
            </Suspense>
          }
        />
        {/* Renamed 2026-09-04 (Crawler Triggers → Crawler Algo). Kept so
            existing bookmarks and links do not 404. */}
        <Route path="crawler-triggers" element={<Navigate to="/crawler-algo" replace />} />
        <Route
          path="leverage-buys"
          element={
            <Suspense fallback={<Loader />}>
              <LeverageBuyTriggersPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<Loader />}>
              <UserManagementPage />
            </Suspense>
          }
        />
        <Route
          path="activity-log"
          element={
            // Admin-only (backend API is super_admin-gated too) — anyone else
            // hitting the URL directly is sent back to the dashboard.
            user?.role !== 'super_admin' ? (
              <Navigate to="/" replace />
            ) : (
              <Suspense fallback={<Loader />}>
                <ActivityLogPage />
              </Suspense>
            )
          }
        />
        <Route
          path="accounts"
          element={
            // Gated by login EMAIL, not role (the two partners only). The page
            // asks /platform/accounts/access itself and sends anyone else home;
            // the API refuses them regardless.
            <Suspense fallback={<Loader />}>
              <AccountManagementPage />
            </Suspense>
          }
        />
        <Route
          path="guides"
          element={
            <Suspense fallback={<Loader />}>
              <GuidesPage />
            </Suspense>
          }
        />
        <Route
          path="documents"
          element={
            // Same partners-only gate as /accounts: the page checks access itself.
            <Suspense fallback={<Loader />}>
              <DocumentsPage />
            </Suspense>
          }
        />
        <Route
          path="tasks"
          element={
            // The work board. Viewers are refused by the API (403 on every
            // route), so the page would render an error rather than a board —
            // send them back rather than show them a wall.
            user?.role === 'viewer' ? (
              <Navigate to="/" replace />
            ) : (
              <Suspense fallback={<Loader />}>
                <TasksPage />
              </Suspense>
            )
          }
        />
        <Route
          path="nightingale"
          element={
            // Managers are blocked from the Nightingale main page (also hidden
            // from the sidebar) — direct URL access redirects to the dashboard.
            user?.role === 'manager' ? (
              <Navigate to="/" replace />
            ) : (
              <Suspense fallback={<Loader />}>
                <NightingaleV2Page />
              </Suspense>
            )
          }
        />
        {/* Old URL — keep bookmarks working */}
        <Route path="nightingale-v2" element={<Navigate to="/nightingale" replace />} />
        <Route path="nightingale-5" element={<Navigate to="/nightingale-5/trades" replace />} />
        <Route
          path="nightingale-5/:tab"
          element={
            <Suspense fallback={<Loader />}>
              <Nightingale5Page />
            </Suspense>
          }
        />
        <Route
          path="least-action"
          element={
            <Suspense fallback={<Loader />}>
              <LeastActionPage />
            </Suspense>
          }
        />
        <Route
          path="last-action"
          element={
            <Suspense fallback={<Loader />}>
              <LastActionPage />
            </Suspense>
          }
        />
        <Route
          path="cluster-action"
          element={
            <Suspense fallback={<Loader />}>
              <ClusterActionPage />
            </Suspense>
          }
        />
        <Route
          path="maximisation"
          element={
            <Suspense fallback={<Loader />}>
              <MaximisationPage />
            </Suspense>
          }
        />
        <Route
          path="pair-2-3"
          element={
            <Suspense fallback={<Loader />}>
              <Pair23Page />
            </Suspense>
          }
        />
        <Route
          path="expiry-transfer"
          element={
            <Suspense fallback={<Loader />}>
              <ExpiryTransferPage />
            </Suspense>
          }
        />
        <Route
          path="index-returns"
          element={
            <Suspense fallback={<Loader />}>
              <IndexReturnsPage />
            </Suspense>
          }
        />
        <Route
          path="dev-workflow"
          element={
            <Suspense fallback={<Loader />}>
              <DevWorkflowPage />
            </Suspense>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const Loader = () => (
  <div className="flex items-center justify-center h-96">
    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
  </div>
);

export default App;
